/**
 * Rebuy Widget Pre-order Handler
 * Sistema para adicionar informações de pre-order aos itens adicionados via widgets do Rebuy
 */

class RebuyWidgetPreorderHandler {
    constructor() {
        this.cache = new Map();
        this.cacheTime = new Map();
        this.apiCalls = new Set();
        this.CACHE_TTL = 30000; // 30 segundos para widgets
        this.DEBOUNCE_DELAY = 500; // 500ms para widgets
        
        this.init();
    }

    init() {
        // Aguardar o Rebuy Engine estar disponível
        this.waitForRebuy().then(() => {
            this.setupEventListeners();
        });
    }

    async waitForRebuy() {
        return new Promise((resolve) => {
            const checkRebuy = () => {
                if (window.Rebuy && window.Rebuy.Cart) {
                    resolve();
                } else {
                    setTimeout(checkRebuy, 100);
                }
            };
            checkRebuy();
        });
    }

    setupEventListeners() {
        // Interceptar outros eventos relevantes
        document.addEventListener('rebuy:cart.add', (event) => {
            this.handleCartAdd(event);
        });
    }

    async handleCartAdd(event) {
        const detail = event.detail;
        if (detail && detail.item) {
            const item = detail.item;
            
            if (item && item.id) {
                  const preOrderInfo = await this.getPreOrderInfo(item.id);
                  
                  if (preOrderInfo && preOrderInfo.message) {
                      this.addPreOrderProperty(item.id, preOrderInfo.message, preOrderInfo.key);
                  }
            }
        }
    }

    async getPreOrderInfo(variantId) {
        try {
            // Verificar cache primeiro
            const cacheKey = `widget_preorder_${variantId}`;
            const cached = this.getCachedData(cacheKey);
            
            // if (cached) {
            //     return cached;
            // }

            // Verificar se já há uma chamada em andamento
            // if (this.apiCalls.has(variantId)) {
            //     return null;
            // }

            //this.apiCalls.add(variantId);

            // Buscar informações de pre-order
            const preOrderData = await this.fetchPreOrderData(variantId);
            
            if (preOrderData) {
                // Cachear resultado
                this.setCachedData(cacheKey, preOrderData);
                return preOrderData;
            }

            return null;

        } catch (error) {
            console.error('Error getting pre-order info:', error);
            return null;
        } finally {
            this.apiCalls.delete(variantId);
        }
    }

    async fetchPreOrderData(variantId) {
        try {
          // Obter IDs das variantes para verificar
          let variantIds = await this.getVariantIds(variantId);
          const url = `https://lionfish-app-zikm2.ondigitalocean.app/api/get-product?variantIds=${variantIds}&variantId=gid://shopify/ProductVariant/${variantId}`;
          const response = await fetch(url);
          const data = await response.json();

          // Verificar se a API retornou dados válidos
          if (data && data.result && Array.isArray(data.result)) {
            const productParent = data?.productParent;

            // Adicionar parentProduct a cada item do resultado (crucial para validação)
            if (productParent && data.result) {
              data.result = data.result.map((item) => ({
                ...item,
                parentProduct: productParent,
              }));
            }

            // Obter product handle se disponível
            const productHandle = await this.getProductHandleByVariantId(
              variantId
            );

            // Processar dados de pre-order com a lógica completa
            const preOrderData = this.findEarliestValidContainerItem(
              data.result,
              variantIds,
              productHandle
            );

            if (preOrderData && preOrderData.stockCalc) {
              return {
                message: preOrderData.template.cartPreOrderSettings.label,
                key: preOrderData.template.cartPreOrderSettings.title,
                stockCalc: preOrderData.stockCalc,
                deliveryDate:
                  preOrderData.template.messageSettings.preOrderMessage,
              };
            }
          } else if (data && data.product === "desativado") {
            return null;
          }

          return null;
        } catch (error) {
            console.error('Error fetching pre-order data:', error);
            return null;
        }
    }

    async getVariantIds(variantId) {
        // Tentar obter product childrens se disponível (na PDP)
        let productChildrens = window.productChildrens?.variants;
        let variantIds = productChildrens?.find(item => item.id == variantId)?.product_childrens;
        
        if (!Array.isArray(variantIds) || variantIds.length === 0) {
            // Se não há product childrens, tentar buscar via handle do produto
            variantIds = await this.getVariantIdsViaHandle(variantId);
        } else {
            // Se há product childrens, incluir todos os IDs
            variantIds = variantIds.map(id => `gid://shopify/ProductVariant/${id}`).join(',');
        }
        
        return variantIds;
    }

    async getVariantIdsViaHandle(variantId) {
        try {
            // Primeiro, precisamos obter o handle do produto
            const productHandle = await this.getProductHandleByVariantId(variantId);
            
            if (!productHandle) {
                console.warn('Could not find product handle for variant:', variantId);
                return `gid://shopify/ProductVariant/${variantId}`;
            }

            // Buscar dados da PDP via handle
            const pdpData = await this.fetchProductDataFromPDP(productHandle);
            
            if (pdpData && pdpData.variants) {
                // Encontrar a variante correspondente e seus product_childrens
                const variant = pdpData.variants.find(v => v.id == variantId);
                if (variant && variant.product_childrens && Array.isArray(variant.product_childrens)) {
                    // Filtrar IDs vazios e converter para o formato correto
                    const validIds = variant.product_childrens.filter(id => id && id.trim() !== '');
                    if (validIds.length > 0) {
                        return validIds.map(id => `gid://shopify/ProductVariant/${id}`).join(',');
                    }
                }
            }

            // Fallback: usar apenas o variant ID atual
            return `gid://shopify/ProductVariant/${variantId}`;

        } catch (error) {
            console.error('Error getting variant IDs via handle:', error);
            return `gid://shopify/ProductVariant/${variantId}`;
        }
    }

    async getProductHandleByVariantId(variantId) {
        try {
            // Buscar informações do produto via API do Shopify
            const response = await fetch(`/products.json?variant_id=${variantId}`);
            const data = await response.json();
            
            if (data && data.products && data.products.length > 0) {
                return data.products[0].handle;
            }
            
            return null;
        } catch (error) {
            console.error('Error getting product handle:', error);
            return null;
        }
    }

    async fetchProductDataFromPDP(productHandle) {
        try {
            // Buscar a página do produto para obter os dados salvos
            const response = await fetch(`/products/${productHandle}`);
            const html = await response.text();
            
            // Criar um elemento temporário para parsear o HTML
            const parser = new DOMParser();
            const doc = parser.parseFromString(html, 'text/html');
            
            // Buscar o script com type="application/pre-order-json"
            const preOrderScript = doc.querySelector('script[type="application/pre-order-json"]');
            
            if (preOrderScript) {
                try {
                    // Extrair o conteúdo JSON do script
                    const scriptContent = preOrderScript.textContent;
                    // Remover a declaração de variável e obter apenas o JSON
                    const jsonMatch = scriptContent.match(/preOrderData\s*=\s*({[\s\S]*});?/);
                    
                    if (jsonMatch) {
                        const productData = JSON.parse(jsonMatch[1]);
                        return productData;
                    }
                } catch (parseError) {
                    console.error('Error parsing pre-order JSON:', parseError);
                }
            }
            
            // Fallback: buscar o script que contém window.productChildrens (método antigo)
            const scripts = doc.querySelectorAll('script');
            for (let script of scripts) {
                const scriptContent = script.textContent;
                if (scriptContent.includes('window.productChildrens')) {
                    // Executar o script para obter window.productChildrens
                    const scriptElement = document.createElement('script');
                    scriptElement.textContent = scriptContent;
                    document.head.appendChild(scriptElement);
                    
                    // Aguardar um pouco para o script executar
                    await new Promise(resolve => setTimeout(resolve, 100));
                    
                    // Verificar se window.productChildrens foi definido
                    if (window.productChildrens) {
                        const productData = window.productChildrens;
                        // Limpar o script temporário
                        document.head.removeChild(scriptElement);
                        return productData;
                    }
                    
                    // Limpar o script temporário
                    document.head.removeChild(scriptElement);
                }
            }
            
            return null;
        } catch (error) {
            console.error('Error fetching product data from PDP:', error);
            return null;
        }
    }

    findEarliestValidContainerItem(items, variantIds = null, productHandle = null) {
        if (!Array.isArray(items) || items.length === 0) return null;
        const currentDate = new Date();

        // Get the number of children from productChildrens to validate container completeness
        const getChildrenCount = (variantIds) => {
            if (typeof variantIds === 'string' && variantIds.includes(',')) {
                return variantIds.split(',').length;
            }
            return 1; // Single variant case
        };

        const containerGroups = items.reduce((groups, item) => {
            const containerId = item.containerId;
            if (!groups[containerId]) {
                groups[containerId] = [];
            }
            groups[containerId].push(item);
            return groups;
        }, {});

        const validContainers = [];

        for (const [containerId, containerItems] of Object.entries(containerGroups)) {
            if (variantIds) {
                const isGotoProduct =
                    productHandle &&
                    (productHandle === 'goto-sofa-three-seater' ||
                        productHandle === 'goto-sofa-loveseat');

                if (!isGotoProduct) {
                    const expectedChildrenCount = getChildrenCount(variantIds);
                    if (containerItems.length !== expectedChildrenCount) {
                        continue;
                    }
                }
            }

            const allItemsValid = containerItems.every(item => {
                if (!item || !item.product || !item.product.container || !item.product.container.ETA) return false;
                if (item.product.container.inUse == false) return false;
                if (item.product.container.statusLocked === true) return false;
                // Validar se a data ETA é maior que a data atual
                const etaDate = new Date(item.product.container.ETA);
                if (etaDate <= currentDate) return false;

                if (item.product.container.startDate) {
                    const startDate = new Date(item.product.container.startDate);
                    if (startDate > currentDate) return false;
                }
                if (item.product.container.endDate) {
                    const endDate = new Date(item.product.container.endDate);
                    if (endDate < currentDate) return false;
                }
                if (item.parentProduct) {
                    const { stockManagement, inventoryQuantity } = item.parentProduct;
                    if (stockManagement === "outOfStock" && inventoryQuantity > 0) {
                        return false;
                    }
                    if (stockManagement === "forceInStock") {
                        return false;
                    }
                }

                const stock = typeof item.stockCalc === 'number' ? item.stockCalc : 0;
                return stock >= 1;
            });

            if (allItemsValid) {
                const earliestItemInContainer = containerItems.reduce((earliest, item) => {
                    const itemDate = new Date(item.product.container.ETA);
                    const earliestDate = new Date(earliest.product.container.ETA);

                    if (itemDate < earliestDate) {
                        return item;
                    } else if (itemDate.getTime() === earliestDate.getTime()) {
                        const itemStock = typeof item.stockCalc === 'number' ? item.stockCalc : Infinity;
                        const earliestStock = typeof earliest.stockCalc === 'number' ? earliest.stockCalc : Infinity;
                        return itemStock < earliestStock ? item : earliest;
                    } else {
                        return earliest;
                    }
                });

                validContainers.push(earliestItemInContainer);
            }
        }
        
        if (validContainers.length === 0) return null;
        
        const earliestItem = validContainers.reduce((earliest, item) => {
            const itemDate = new Date(item.product.container.ETA);
            const earliestDate = new Date(earliest.product.container.ETA);

            if (itemDate < earliestDate) {
                return item;
            } else if (itemDate.getTime() === earliestDate.getTime()) {
                const itemStock = typeof item.stockCalc === 'number' ? item.stockCalc : Infinity;
                const earliestStock = typeof earliest.stockCalc === 'number' ? earliest.stockCalc : Infinity;
                return itemStock < earliestStock ? item : earliest;
            } else {
                return earliest;
            }
        });

        // Register stock limits in localStorage for performance
        if (earliestItem && typeof earliestItem.stockCalc === 'number') {
            try {
                const stockLimitsCache = JSON.parse(localStorage.getItem('preOrderStockLimits') || '{}');
                
                // Create a cache key based on variant IDs
                const cacheKey = Array.isArray(variantIds) ? variantIds.join(',') : String(variantIds);
                
                stockLimitsCache[cacheKey] = {
                    stockLimit: earliestItem.stockCalc,
                    productHandle: productHandle,
                    containerId: earliestItem.containerId,
                    ETA: earliestItem.product.container.ETA,
                    cachedAt: new Date().toISOString()
                };
                
                setTimeout(() => {
                    localStorage.setItem('preOrderStockLimits', JSON.stringify(stockLimitsCache));
                }, 1000);
                
            } catch (error) {
                console.error('❌ Error caching stock limits:', error);
            }
        }

        return earliestItem;
    }

    async addPreOrderProperty(variantId, message, key) {
        try {
            const Cart = window.Rebuy.Cart;
            
            // Encontrar o item no carrinho
            const items = Cart.getItems();
            const item = items.find(item => item.variant_id == variantId);
            
            if (!item) {
                console.warn('Item not found in cart for variant:', variantId);
                return;
            }

            // Adicionar propriedade de pre-order
            Cart.changeItem({
                id: item.key,
                quantity: item.quantity,
                properties: {
                    ...item.properties,
                    [key]: message
                }
            }, {
                success: () => {
                    console.log('Pre-order property added successfully');
                },
                error: (error) => {
                    console.error('Error adding pre-order property:', error);
                }
            });

        } catch (error) {
            console.error('Error adding pre-order property:', error);
        }
    }

    getCachedData(key) {
        const cached = this.cache.get(key);
        const cacheTime = this.cacheTime.get(key);
        
        if (cached && cacheTime && (Date.now() - cacheTime < this.CACHE_TTL)) {
            return cached;
        }
        
        // Limpar cache expirado
        if (cached) {
            this.cache.delete(key);
            this.cacheTime.delete(key);
        }
        
        return null;
    }

    setCachedData(key, data) {
        this.cache.set(key, data);
        this.cacheTime.set(key, Date.now());
    }
}

// Inicializar quando o DOM estiver pronto
document.addEventListener('DOMContentLoaded', () => {
    new RebuyWidgetPreorderHandler();
});

// Também inicializar se o DOM já estiver carregado
if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
        new RebuyWidgetPreorderHandler();
    });
} else {
    new RebuyWidgetPreorderHandler();
}
